import React from 'react';

interface YearTransitionBannerProps {
  nextYear: number;
}

const YEAR_CONTEXT: Record<number, { quote: string; guardian: string }> = {
  2020: { quote: 'A global pandemic has just begun. Humanity is about to face its greatest test of cooperation. Let us see if you understand why.', guardian: 'Guardian of Partnership' },
  2021: { quote: 'Inequalities have deepened. The gap between rich and poor grew wider this year with the highest 10% of Australian households holding 46% of wealth.', guardian: 'Guardian of People' },
  2022: { quote: "Antarctic sea ice dropped to its lowest level ever recorded in 2022. Earth's climate systems are speaking. Is anyone listening?", guardian: 'Guardian of Planet' },
  2023: { quote: 'Over 110 million people are forcibly displaced. The largest number ever recorded.', guardian: 'Guardian of Peace' },
  2024: { quote: 'Renewable energy capacity additions surged to 666 GW. The momentum is real, but tripling by 2030 still requires more.', guardian: 'Guardian of Prosperity' },
  2025: { quote: 'Halfway to 2030. Only 35% of SDG targets are on track. The Council is watching closely.', guardian: 'Guardian of Partnership' },
  2026: { quote: 'Biodiversity loss continues. One million species face extinction. Earth must do better.', guardian: 'Guardian of Planet' },
  2027: { quote: 'Three years remain. The window is narrowing. Every answer matters more than it did last year.', guardian: 'Guardian of People' },
  2028: { quote: 'Two years. The Council has seen civilisations fail at this point. Earth will not. We believe this.', guardian: 'Guardian of Peace' },
  2029: { quote: "This is it. Ten years of hearings. The fate of Earth's application rests on what happens today.", guardian: 'Guardian of Prosperity' },
};

const YearTransitionBanner: React.FC<YearTransitionBannerProps> = ({ nextYear }) => {
  const calendarYear = 2020 + nextYear - 1;
  const context = YEAR_CONTEXT[calendarYear];

  return (
    <div
      className="relative flex items-center justify-center w-full"
      style={{
        width: '100%',
        padding: '18px 0',
      }}
    >
      {/* Main banner panel that holds the year transition title. */}
      <div
        className="relative flex items-center justify-center w-full h-full overflow-hidden"
        style={{
          width: '100%',
          borderRadius: 0,
          background: 'rgba(31, 42, 74, 0.85)',
          border: '1px solid rgba(46, 58, 107, 0.9)',
          boxShadow: '0 10px 24px 0 rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Top glow line framing the banner title. */}
        <div
          className="absolute left-0 right-0 top-4 mx-auto"
          style={{
            width: 'calc(100% - 32px)',
            height: '1px',
            background: 'linear-gradient(90deg, rgba(14, 133, 120, 0.00) 0%, #14B8A6 49.52%, rgba(9, 82, 74, 0.00) 100%)',
            boxShadow: '0 4px 4px 0 rgba(20, 184, 166, 0.40)',
          }}
        />
        {/* Bottom glow line to visually bracket the transition text. */}
        <div
          className="absolute left-0 right-0 bottom-4 mx-auto"
          style={{
            width: 'calc(100% - 32px)',
            height: '1px',
            background: 'linear-gradient(90deg, rgba(14, 133, 120, 0.00) 0%, #14B8A6 49.52%, rgba(9, 82, 74, 0.00) 100%)',
            boxShadow: '0 4px 4px 0 rgba(20, 184, 166, 0.40)',
          }}
        />

        {/* Title + quote */}
        <div className="relative z-10 flex flex-col items-center gap-3 px-10 py-6 text-center">
          <h1
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '55px',
              fontWeight: 400,
              lineHeight: 'normal',
              color: '#FFF',
            }}
          >
            <span>Year {nextYear} of Trial: </span>
            <span style={{ color: '#FFE896' }}>{calendarYear}</span>
          </h1>

          {context && (
            <div className="flex flex-col items-center gap-1 max-w-2xl">
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.6', fontStyle: 'italic' }}>
                "{context.quote}"
              </p>
              <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '15px', color: '#FFE896' }}>
                — {context.guardian}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YearTransitionBanner;